import type {ChangeEvent} from 'react';
import clsx from 'clsx';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';

import {Heading} from '@/modules/foundation/components/typography/heading';

import type {Filters} from './search-params-filter';
import {
	filtersToSearchParams,
	searchParamsToFilters,
} from './search-params-filter';

export type FieldOfAreaFilters = {
	slug: string;
	title: string;
	_id: string;
};

export type AreaFilters = {
	slug: string;
	title: string;
};

type Props = {
	fieldOfAreaFilters?: FieldOfAreaFilters[];
	areaFilters?: AreaFilters[];
	hideGenereltFilter?: boolean;
};

export const CategoryFilters = ({
	fieldOfAreaFilters,
	areaFilters,
	hideGenereltFilter,
}: Props) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const router = useRouter();

	const filters: Filters = searchParamsToFilters(searchParams);

	const handleFieldOfAreaChange = (
		e: ChangeEvent<HTMLInputElement>,
		slug: string,
	) => {
		const checked = e.target.checked;

		if (checked) {
			filters.fieldOfArea.push(slug);
		} else {
			filters.fieldOfArea = filters.fieldOfArea.filter(
				(item) => item !== slug,
			);
		}

		filters.area = [];

		const searchParams = filtersToSearchParams(filters);

		router.push(`${pathname}?${searchParams.toString()}`, {scroll: false});
	};

	const handleAreaChange = (
		e: ChangeEvent<HTMLInputElement>,
		slug: string,
	) => {
		const checked = e.target.checked;

		if (checked) {
			filters.area.push(slug);
		} else {
			filters.area = filters.area.filter((item) => item !== slug);
		}

		const searchParams = filtersToSearchParams(filters);

		router.push(`${pathname}?${searchParams.toString()}`, {scroll: false});
	};

	return (
		<form className={clsx('text-sm')}>
			<fieldset className={clsx('mb-8')}>
				<Heading asChild size="sm" spacing>
					<legend>Velg kategori</legend>
				</Heading>
				<div
					className={clsx(
						'flex',
						'flex-wrap',
						'gap-4',
						'@2xl:flex-col',
						'items-start',
					)}
				>
					{!hideGenereltFilter && (
						<label
							className={clsx(
								'inline-flex',
								'items-center',
								'gap-2',
							)}
						>
							Generelt
							<input
								type="checkbox"
								className={clsx(
									'-order-1',
									'border-grey-45',
									'rounded-[4px]',
									'size-[21px]',
									'checked:bg-primary-25',
								)}
								// FIXME: fix react hooks eslint errors
								// eslint-disable-next-line react-hooks/immutability
								onChange={(e) =>
									handleFieldOfAreaChange(e, 'generelt')
								}
								checked={filters.fieldOfArea.includes(
									'generelt',
								)}
							/>
						</label>
					)}

					{fieldOfAreaFilters?.map((area) => (
						<label
							key={area.slug}
							className={clsx(
								'inline-flex',
								'items-center',
								'gap-2',
							)}
						>
							{area.title}
							<input
								type="checkbox"
								className={clsx(
									'-order-1',
									'border-grey-45',
									'rounded-[4px]',
									'size-[21px]',
									'checked:bg-primary-25',
								)}
								onChange={(e) =>
									handleFieldOfAreaChange(e, area._id)
								}
								checked={filters.fieldOfArea.includes(area._id)}
							/>
						</label>
					))}
				</div>
			</fieldset>
			{areaFilters?.length ? (
				<fieldset>
					<Heading asChild size="sm" spacing>
						<legend>Tema</legend>
					</Heading>
					<div
						className={clsx(
							'flex',
							'flex-wrap',
							'gap-4',
							'@2xl:flex-col',
							'items-start',
						)}
					>
						{areaFilters.map((area) => (
							<label
								key={area.slug}
								className={clsx(
									'inline-flex',
									'items-center',
									'gap-3',
								)}
							>
								{area.title}
								<input
									type="checkbox"
									className={clsx(
										'-order-1',
										'border-grey-45',
										'rounded-[4px]',
										'size-[21px]',
										'checked:bg-primary-25',
									)}
									onChange={(e) =>
										handleAreaChange(e, area.slug)
									}
									checked={filters.area.includes(area.slug)}
								/>
							</label>
						))}
					</div>
				</fieldset>
			) : null}
		</form>
	);
};
